import { cn } from '@/lib/cn';
import { pickAvatarByRole } from '@/assets/builder-illustrations';

interface AvatarProps {
  name: string;
  src?: string | null;
  /** User role — used to pick an Ideogram illustration fallback when src
   *  is absent. Maps to supplier/foreman/pm/architect/owner buckets.
   *  Defaults to 'pm' when omitted. */
  role?: string | null;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

const sizes = {
  sm: 'h-7 w-7 text-[11px]',
  md: 'h-9 w-9 text-sm',
  lg: 'h-12 w-12 text-base',
} as const;

function initials(name: string): string {
  const parts = name.trim().split(/\s+/);
  const first = parts[0]?.[0] ?? '?';
  const second = parts.length > 1 ? parts[parts.length - 1]![0] : '';
  return (first + second).toUpperCase();
}

/**
 * Avatar uses the primary-ink swatch for its background so it naturally
 * inverts in dark mode (dark chip on light screen, light chip on dark
 * screen). The halo (`ring`) picks up surface-2 so the chip always feels
 * lifted off whatever it sits on.
 *
 * When `src` is absent and a `role` is provided, falls back to the
 * matching Ideogram isometric illustration (Wave 80) before falling back
 * to text initials.
 */
export function Avatar({ name, src, role, size = 'md', className }: AvatarProps) {
  const fallback = pickAvatarByRole(role);
  const imgSrc = src || fallback.src;

  return (
    <div
      className={cn(
        'inline-flex items-center justify-center rounded-full bg-ink text-surface font-semibold overflow-hidden ring-2 ring-surface-2',
        sizes[size],
        className,
      )}
      aria-label={name}
    >
      <img src={imgSrc} alt={name} className="h-full w-full object-cover" />
    </div>
  );
}
